import React, { useMemo } from 'react';
import { Typography, Tooltip, withStyles, Link } from '@material-ui/core';
import DataGrid, {
  Column,
  Selection,
  Sorting,
  Scrolling
} from 'devextreme-react/data-grid';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { Error as ErrorIcon, Info as InfoIcon, Warning as WarningIcon, CheckCircle as SuccessIcon } from "@material-ui/icons";
import { Constants } from 'utilities/constants';
import { Tooltip as DevExTooltip } from 'devextreme-react/tooltip';
import columnSchema from 'components/underwrite/acquisition/propertyUnderwrite/header/propertyHistory/opportunityHistoryColumnSchema';

const styles = theme => ({
  root: {
    // marginTop: 16,
    // marginBottom: 8
    // margin: '0px 8px 8px'
    minHeight: 152
  },
  title: {
    marginBottom: 6,
    // marginLeft: 8
  },
  grid: {
    maxHeight: 175
  },
  noBidReason: {
    color: theme.palette.warning.text,
    fontWeight: 'bold'
  },
  doNotBuyWarning: {
    color: theme.palette.red.dark,
    fontWeight: 'bold'
  },
  highFidelityFieldsWarning: {
    color: "#5baeff",
    fontWeight: 'bold'
  },
});

const excludedReasons = ['Below % of List Threshold']; // Exclude no bid reason when value is Below % of List

const hasNoBidReason = opp => {
  return opp.noBidReason != null && !excludedReasons.includes(opp.noBidReason);
}

const hasDoNotBuyWarning = opp => {
  return opp.crmTerminationReason === 'Do Not Buy';
}

const hasHighFidelityFieldsWarning = (opp, currentOppId) => {
  if (opp.id === currentOppId) return false;
  //return opp.crmProposedRepairBidAmount != null || opp.sellerHoa != null || opp.sellerTaxes != null;
  return opp.crmProposedRepairBidAmount != null || opp.sellerHoa != null; //EF 20250721 temp removal of seller taxes
}

const PropertyHistoryOpportunitiesGrid = ({ classes, location, txnOppID, opportunities, onTitleClick }) => {
  const onCellPrepared = cell => {
    if (cell.rowType === 'data' && cell.column.dataField === 'noBidReason' && hasNoBidReason(cell.data)) {
      cell.cellElement.className = classes.noBidReason;
    }
    if (cell.rowType === 'data' && cell.column.dataField === 'crmTerminationReason' && hasDoNotBuyWarning(cell.data)) {
      cell.cellElement.className = classes.doNotBuyWarning;
    }
    //if (cell.rowType === 'data' && ['crmProposedRepairBidAmount','sellerHoa','sellerTaxes'].includes(cell.column.dataField) && hasHighFidelityFieldsWarning(cell.data, txnOppID)) {
    if (cell.rowType === 'data' && ['crmProposedRepairBidAmount','sellerHoa'].includes(cell.column.dataField) && hasHighFidelityFieldsWarning(cell.data, txnOppID)) {  //EF 20250721 temp removal of seller taxes      
      cell.cellElement.className = classes.highFidelityFieldsWarning;
    }
  }

  const onViewOppClick = (id) => {
    const url = `${window.location.origin}${Constants.Routes.UNDERWRITE_PROPERTY}/${id}`;
    window.open(url, '_blank');
  };

  // Custom cell render function for the "Opportunity ID" column
  const renderOppLink = (cellInfo) => {
    const isCurrentOpp = cellInfo.value === txnOppID;
    if (isCurrentOpp) {
      return (
        <span>
          {cellInfo.text + ' ⬅️'}
        </span>
      )
    }
    return (
      <Link
        component="button" // Treat as a button for accessibility/focus
        variant="body2" // Use a typography variant for text styling
        onClick={() => onViewOppClick(cellInfo.value)} // Pass the row data
        color={'primary'}
        style={{ fontSize: 'inherit', lineHeight: 'inherit', verticalAlign: 'initial' }}
      >
        {cellInfo.text}
      </Link>
    );
  };

  const renderValuationNotesCell = (cellInfo) => {
    const text = cellInfo.value || '';  
  
    const ellipsisStyle = {
      whiteSpace:    'nowrap',
      overflow:      'hidden',
      textOverflow:  'ellipsis',
      display:       'block',
      width:         '100%',
      cursor:        'help'
    };
  
    return (
      <Tooltip title={text} arrow>
        <span style={ellipsisStyle}>
          {text}
        </span>
      </Tooltip>
    );
  }

  const getTitleColorClass = () => {
    if (opportunities.some(opp => hasDoNotBuyWarning(opp))) { // RED (Do Not Buy) takes absolute precedence
      return classes.doNotBuyWarning;
    } else if (opportunities.some(opp => hasNoBidReason(opp))) { // if not RED, check YELLOW (No Bid Reason)
      return classes.noBidReason;
    } else if (opportunities.some(opp => hasHighFidelityFieldsWarning(opp, txnOppID))) { // if neither RED nor YELLOW, check BLUE (High Fidelity)
      return classes.highFidelityFieldsWarning;
    } else return null;
  }

  const getWarningIcons = () => {
    const icons = [];
    if (opportunities.some(opp => hasDoNotBuyWarning(opp))) {
      icons.push(
        <Tooltip title='Do Not Buy warning'>
          <WarningIcon fontSize='small' className={classes.doNotBuyWarning} style={{ marginLeft: 6, marginBottom: 6 }} />
        </Tooltip>
      );
    };
    if (opportunities.some(opp => hasNoBidReason(opp))) {
      icons.push(
        <Tooltip title='No Bid Reason warning'>
          <WarningIcon fontSize='small' className={classes.noBidReason} style={{ marginLeft: 6, marginBottom: 6 }} />
        </Tooltip>
      );
    };
    if (opportunities.some(opp => hasHighFidelityFieldsWarning(opp, txnOppID))) {
      icons.push(
        <Tooltip title='Historical fields available'>
          <WarningIcon fontSize='small' className={classes.highFidelityFieldsWarning} style={{ marginLeft: 6, marginBottom: 6 }} />
        </Tooltip>
      );
    };
    return icons;
  }

  const getIdText = cellInfo => {
    if (cellInfo.value === txnOppID) {
      return cellInfo.valueText + ' ⬅️';
    }
    return cellInfo.valueText;
  }

  let tooltips = useMemo(() => {
    return columnSchema.filter(col => col.tooltip != null).map(({
      field,
      displayName,
      tooltip
    }) => {
      const target = `div#opportunityHistoryGrid td[aria-label='Column ${displayName}']`;
      return (
        <DevExTooltip
          key={field}
          target={target}
          showEvent="dxhoverstart"
          hideEvent="dxhoverend"
          position="top"
        >
          <p>{tooltip}</p>
        </DevExTooltip>
      )
    })
  }, []);

  return (
    <div className={classes.root}>
      {
        opportunities && opportunities?.length > 0 ?
          <>
            <DataGrid
              id='opportunityHistoryGrid'
              dataSource={opportunities}
              showBorders={true}
              showRowLines={true}
              showColumnLines={true}
              hoverStateEnabled={true}
              allowColumnResizing={true}
              className={classes.grid}
              columnAutoWidth={true}
              onCellPrepared={onCellPrepared}
              width='100%'
              height={location.pathname === Constants.Routes.EXPLORE ? undefined : 152}
            >
              <Scrolling mode='virtual' />
              <Sorting mode={'multiple'} />
              <Selection mode='single' />
              {columnSchema.map(({ field, displayName, width, type, fixed, format, sortIndex, sortOrder }) => {
                return <Column
                  key={field}
                  dataField={field}
                  caption={displayName}
                  width={width}
                  dataType={type}
                  format={format}
                  fixed={fixed}
                  sortIndex={sortIndex}
                  sortOrder={sortOrder}
                  cellRender={field === 'notes' ? renderValuationNotesCell : 
                                field === 'id' ? renderOppLink : null}
                />
              })}
            </DataGrid>
            {tooltips}
          </>
          :
          <Typography className={classes.title} variant="subtitle2">
            No opportunities
          </Typography>
      }
    </div>
  );
};

export default compose(withRouter, withStyles(styles))(PropertyHistoryOpportunitiesGrid);
